import { Bot, Check, ChevronLeft, ChevronRight, Code2, Database, Images, Rocket, ShoppingBag, Sparkles, Video, WandSparkles, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useStudio } from "../studioStore";
import type { StudioView } from "../types";
import { BLACKED_ONBOARDING_KEY, BLACKED_START_EVENT } from "./blackedEvents";

type CreationKind = "website" | "system";
type WebsiteGoal = "presence" | "sales" | "leads" | "community";
type WebsiteStyle = "minimal" | "bold" | "elegant" | "playful";
type WebsiteModule = "store" | "gallery" | "video" | "contact";

const WEBSITE_MODULES: Array<{ id: WebsiteModule; icon: typeof ShoppingBag }> = [
  { id: "store", icon: ShoppingBag },
  { id: "gallery", icon: Images },
  { id: "video", icon: Video },
  { id: "contact", icon: Code2 },
];

function hasFinishedOnboarding() {
  try { return localStorage.getItem(BLACKED_ONBOARDING_KEY) === "complete"; }
  catch { return false; }
}

export function BlackedOnboarding() {
  const { t } = useTranslation();
  const { addComponent, addTable, addWorkflow, logActivity, setView } = useStudio();
  const [open, setOpen] = useState(() => !hasFinishedOnboarding());
  const [step, setStep] = useState(0);
  const [kind, setKind] = useState<CreationKind>("website");
  const [name, setName] = useState("");
  const [goal, setGoal] = useState<WebsiteGoal>("presence");
  const [audience, setAudience] = useState("");
  const [style, setStyle] = useState<WebsiteStyle>("minimal");
  const [modules, setModules] = useState<WebsiteModule[]>(["gallery", "contact"]);
  const [created, setCreated] = useState(false);

  useEffect(() => {
    const restart = () => {
      setStep(0); setKind("website"); setName(""); setGoal("presence"); setAudience("");
      setStyle("minimal"); setModules(["gallery", "contact"]); setCreated(false); setOpen(true);
    };
    window.addEventListener(BLACKED_START_EVENT, restart);
    return () => window.removeEventListener(BLACKED_START_EVENT, restart);
  }, []);

  const needsData = kind === "system" || modules.includes("store") || modules.includes("contact");
  const journey = useMemo(() => needsData
    ? ["designer", "data", "assistant", "deploy"] as StudioView[]
    : ["designer", "assistant", "deploy"] as StudioView[], [needsData]);
  const recommendationKeys = useMemo(() => ["foundation", ...modules] as Array<"foundation" | WebsiteModule>, [modules]);

  function persistComplete() {
    try { localStorage.setItem(BLACKED_ONBOARDING_KEY, "complete"); } catch { /* Disponible en la sesión actual. */ }
  }

  function close() {
    // "Ahora no" conserva la invitación para el siguiente ingreso. Sólo una creación
    // terminada marca el recorrido como completo; el botón flotante siempre permite repetirlo.
    if (created) persistComplete();
    setOpen(false);
  }

  function makeComponent(item: Omit<Parameters<typeof addComponent>[0], "id">) {
    return { ...item, id: crypto.randomUUID() };
  }

  function addSystemData() {
    addTable({ id: crypto.randomUUID(), name: `items_${Date.now().toString(36)}`, label: t("blacked.system.tableLabel"), writeRole: "editor", fields: [
      { id: crypto.randomUUID(), name: "id", type: "integer", required: true, primary: true },
      { id: crypto.randomUUID(), name: "name", type: "text", required: true },
      { id: crypto.randomUUID(), name: "status", type: "text", required: true },
    ] });
    addWorkflow({ id: crypto.randomUUID(), name: t("blacked.system.workflowName"), method: "POST", route: `/api/items-${Date.now().toString(36)}`, actions: [
      { id: crypto.randomUUID(), kind: "input", label: t("blacked.system.receive"), detail: "name, status", enabled: true },
      { id: crypto.randomUUID(), kind: "validate", label: t("blacked.system.validate"), detail: "name required", enabled: true },
      { id: crypto.randomUUID(), kind: "response", label: t("blacked.system.respond"), detail: "{ ok: true }", enabled: true },
    ] });
  }

  function addWebsiteData() {
    const suffix = Date.now().toString(36);
    if (modules.includes("store")) {
      addTable({ id: crypto.randomUUID(), name: `products_${suffix}`, label: t("blackedInterview.tables.products"), writeRole: "editor", fields: [
        { id: crypto.randomUUID(), name: "id", type: "integer", required: true, primary: true },
        { id: crypto.randomUUID(), name: "name", type: "text", required: true },
        { id: crypto.randomUUID(), name: "price", type: "decimal", required: true },
        { id: crypto.randomUUID(), name: "description", type: "text", required: false },
        { id: crypto.randomUUID(), name: "active", type: "boolean", required: true },
      ] });
      addWorkflow({ id: crypto.randomUUID(), name: t("blackedInterview.tables.orderWorkflow"), method: "POST", route: `/api/orders-${suffix}`, actions: [
        { id: crypto.randomUUID(), kind: "input", label: t("blacked.system.receive"), detail: "product_id, quantity, customer", enabled: true },
        { id: crypto.randomUUID(), kind: "validate", label: t("blacked.system.validate"), detail: "product_id and quantity required", enabled: true },
        { id: crypto.randomUUID(), kind: "response", label: t("blacked.system.respond"), detail: "{ ok: true, status: 'pending' }", enabled: true },
      ] });
    }
    if (modules.includes("contact")) {
      addTable({ id: crypto.randomUUID(), name: `contacts_${suffix}`, label: t("blackedInterview.tables.contacts"), writeRole: "editor", fields: [
        { id: crypto.randomUUID(), name: "id", type: "integer", required: true, primary: true },
        { id: crypto.randomUUID(), name: "name", type: "text", required: true },
        { id: crypto.randomUUID(), name: "email", type: "text", required: true },
        { id: crypto.randomUUID(), name: "message", type: "text", required: true },
      ] });
    }
  }

  function createStarter() {
    if (created) return;
    const creationName = name.trim() || t(`blacked.defaults.${kind}`);
    if (kind === "system") {
      [
        makeComponent({ kind: "hero", name: t("blacked.blocks.heroName"), title: creationName, body: t("blacked.blocks.systemHero"), actionLabel: t("blacked.blocks.cta"), alignment: "left", tone: "accent" }),
        makeComponent({ kind: "cards", name: t("blacked.blocks.cardsName"), title: t("blacked.blocks.cardsTitle"), body: t("blacked.blocks.systemCards"), columns: 3, tone: "default" }),
        makeComponent({ kind: "form", name: t("blacked.blocks.formName"), title: t("blacked.blocks.formTitle"), body: t("blacked.blocks.formBody"), actionLabel: t("blacked.blocks.formAction"), tone: "muted" }),
        makeComponent({ kind: "footer", name: t("blacked.blocks.footerName"), title: creationName, body: t("blacked.blocks.footerBody"), tone: "dark" }),
      ].forEach(addComponent);
      addSystemData();
    } else {
      const targetAudience = audience.trim() || t("blackedInterview.audienceFallback");
      const websiteBlocks = [
        makeComponent({ kind: "navbar", name: t("blackedInterview.blocks.navName"), title: creationName, body: t("blackedInterview.blocks.navLinks"), actionLabel: t("blackedInterview.blocks.navAction"), tone: "dark" }),
        makeComponent({ kind: "hero", name: t("blacked.blocks.heroName"), title: creationName, body: t(`blackedInterview.heroBodies.${goal}`, { audience: targetAudience }), actionLabel: t("blacked.blocks.cta"), alignment: style === "minimal" ? "left" : "center", tone: "accent", animation: "fade" as const }),
        makeComponent({ kind: "cards", name: t("blacked.blocks.cardsName"), title: t("blacked.blocks.cardsTitle"), body: t("blacked.blocks.websiteCards"), actionLabel: t("blacked.blocks.cta"), columns: 3, tone: "default", animation: "slide-up" as const }),
      ];
      if (modules.includes("store")) websiteBlocks.push(makeComponent({ kind: "cards", name: t("blackedInterview.blocks.storeName"), title: t("blackedInterview.blocks.storeTitle"), body: t("blackedInterview.blocks.storeItems"), actionLabel: t("blackedInterview.blocks.storeAction"), columns: 3, tone: "default", animation: "slide-up" }));
      if (modules.includes("gallery")) websiteBlocks.push(makeComponent({ kind: "cards", name: t("blackedInterview.blocks.galleryName"), title: t("blackedInterview.blocks.galleryTitle"), body: [1, 2, 3].map((index) => `${t("blackedInterview.blocks.galleryName")} 0${index}`).join("|"), actionLabel: t("blackedInterview.blocks.navAction"), columns: 3, tone: "muted", animation: "zoom-in" }));
      if (modules.includes("video")) websiteBlocks.push(makeComponent({ kind: "video", name: t("blackedInterview.blocks.videoName"), title: t("blackedInterview.blocks.videoTitle"), body: t("blackedInterview.blocks.videoBody"), tone: "dark", alignment: "left", animation: "fade" }));
      if (modules.includes("contact")) websiteBlocks.push(makeComponent({ kind: "form", name: t("blacked.blocks.formName"), title: t("blackedInterview.blocks.contactTitle"), body: t("blackedInterview.blocks.contactBody"), actionLabel: t("blackedInterview.blocks.contactAction"), tone: "muted", animation: "slide-up" }));
      websiteBlocks.push(makeComponent({ kind: "footer", name: t("blacked.blocks.footerName"), title: creationName, body: t("blacked.blocks.footerBody"), tone: "dark" }));
      websiteBlocks.forEach(addComponent);
      addWebsiteData();
    }
    logActivity(t("blacked.activityTitle"), t("blacked.activityBody", { name: creationName }), "success");
    setCreated(true);
    setStep(5);
  }

  function toggleModule(module: WebsiteModule) {
    setModules((current) => current.includes(module) ? current.filter((item) => item !== module) : [...current, module]);
  }

  function openJourneyView(index: number) {
    setView(journey[index]);
    setStep(6 + index);
  }

  if (!open) return (
    <button className="blacked-launcher" onClick={() => setOpen(true)} aria-label={t("blacked.open")} title={t("blacked.open")}>
      <Bot size={22}/><span>blacked</span>
    </button>
  );

  const journeyIndex = Math.max(0, step - 6);
  const isJourney = step >= 6;
  const isLastJourney = isJourney && journeyIndex === journey.length - 1;
  const totalSteps = kind === "website" ? journey.length + 6 : journey.length + 3;
  const progressStep = kind === "website" ? step + 1 : Math.min(step, 2) + 1;

  return <aside className="blacked-panel" role="dialog" aria-modal="true" aria-labelledby="blacked-title">
    <header className="blacked-header">
      <div className="blacked-avatar" aria-hidden="true"><Bot size={24}/><Sparkles size={12}/></div>
      <div><small>{t("blacked.kicker")}</small><strong>blacked</strong></div>
      <button onClick={close} aria-label={t("blacked.close")}><X size={17}/></button>
    </header>

    <div className="blacked-progress" aria-label={t("blacked.progress")}><span style={{ width: `${Math.min(100, (progressStep / totalSteps) * 100)}%` }}/></div>

    <div className="blacked-body">
      {step === 0 && <>
        <span className="blacked-bubble">{t("blacked.hello")}</span>
        <h2 id="blacked-title">{t("blacked.welcomeTitle")}</h2>
        <p>{t("blacked.welcomeBody")}</p>
        <button className="btn primary" onClick={() => setStep(1)}>{t("blacked.letsGo")}<ChevronRight size={15}/></button>
        <button className="blacked-text-button" onClick={close}>{t("blacked.later")}</button>
      </>}

      {step === 1 && <>
        <h2 id="blacked-title">{t("blacked.chooseTitle")}</h2><p>{t("blacked.chooseBody")}</p>
        <div className="blacked-kind-grid">
          <button className={kind === "website" ? "is-active" : ""} onClick={() => setKind("website")}><Code2/><strong>{t("blacked.website")}</strong><small>{t("blacked.websiteHint")}</small></button>
          <button className={kind === "system" ? "is-active" : ""} onClick={() => setKind("system")}><Database/><strong>{t("blacked.systemOption")}</strong><small>{t("blacked.systemHint")}</small></button>
        </div>
        {kind === "system" && <label className="blacked-name">{t("blacked.nameLabel")}<input autoFocus value={name} onChange={(event) => setName(event.target.value)} placeholder={t("blacked.defaults.system")} maxLength={80}/></label>}
        <button className="btn primary" onClick={() => kind === "website" ? setStep(2) : createStarter()}><WandSparkles size={15}/>{kind === "website" ? t("blackedInterview.start") : t("blacked.create")}</button>
      </>}

      {step === 2 && <>
        <span className="blacked-bubble">{t("blackedInterview.questionCounter", { current: 1, total: 2 })}</span>
        <h2 id="blacked-title">{t("blackedInterview.briefTitle")}</h2><p>{t("blackedInterview.briefBody")}</p>
        <div className="blacked-form-grid">
          <label>{t("blacked.nameLabel")}<input autoFocus value={name} onChange={(event) => setName(event.target.value)} placeholder={t("blacked.defaults.website")} maxLength={80}/></label>
          <label>{t("blackedInterview.goalLabel")}<select value={goal} onChange={(event) => setGoal(event.target.value as WebsiteGoal)}>{(["presence", "sales", "leads", "community"] as WebsiteGoal[]).map((item) => <option key={item} value={item}>{t(`blackedInterview.goals.${item}`)}</option>)}</select></label>
          <label>{t("blackedInterview.audienceLabel")}<input value={audience} onChange={(event) => setAudience(event.target.value)} placeholder={t("blackedInterview.audiencePlaceholder")} maxLength={120}/></label>
          <label>{t("blackedInterview.styleLabel")}<select value={style} onChange={(event) => setStyle(event.target.value as WebsiteStyle)}>{(["minimal", "bold", "elegant", "playful"] as WebsiteStyle[]).map((item) => <option key={item} value={item}>{t(`blackedInterview.styles.${item}`)}</option>)}</select></label>
        </div>
        <div className="blacked-actions"><button className="btn secondary" onClick={() => setStep(1)}><ChevronLeft size={15}/>{t("blackedInterview.back")}</button><button className="btn primary" onClick={() => setStep(3)}>{t("blackedInterview.continue")}<ChevronRight size={15}/></button></div>
      </>}

      {step === 3 && <>
        <span className="blacked-bubble">{t("blackedInterview.questionCounter", { current: 2, total: 2 })}</span>
        <h2 id="blacked-title">{t("blackedInterview.modulesTitle")}</h2><p>{t("blackedInterview.modulesBody")}</p>
        <div className="blacked-module-grid">
          {WEBSITE_MODULES.map(({ id, icon: Icon }) => <button key={id} type="button" className={modules.includes(id) ? "is-active" : ""} aria-pressed={modules.includes(id)} onClick={() => toggleModule(id)}><Icon size={18}/><span><strong>{t(`blackedInterview.modules.${id}.name`)}</strong><small>{t(`blackedInterview.modules.${id}.hint`)}</small></span><Check size={15}/></button>)}
        </div>
        <div className="blacked-actions"><button className="btn secondary" onClick={() => setStep(2)}><ChevronLeft size={15}/>{t("blackedInterview.back")}</button><button className="btn primary" onClick={() => setStep(4)}>{t("blackedInterview.recommend")}<ChevronRight size={15}/></button></div>
      </>}

      {step === 4 && <>
        <span className="blacked-success"><Sparkles size={18}/></span>
        <h2 id="blacked-title">{t("blackedInterview.reviewTitle")}</h2><p>{t("blackedInterview.reviewBody", { name: name.trim() || t("blacked.defaults.website") })}</p>
        <div className="blacked-recommendations" aria-label={t("blackedInterview.recommendationTitle")}>
          {recommendationKeys.map((item) => <div key={item}><Check size={14}/><span>{t(`blackedInterview.recommendations.${item}`)}</span></div>)}
        </div>
        <div className="blacked-actions"><button className="btn secondary" onClick={() => setStep(3)}><ChevronLeft size={15}/>{t("blackedInterview.back")}</button><button className="btn primary" onClick={createStarter}><WandSparkles size={15}/>{t("blackedInterview.createRecommended")}</button></div>
      </>}

      {step === 5 && <>
        <span className="blacked-success"><Check size={18}/></span>
        <h2 id="blacked-title">{t("blacked.createdTitle")}</h2><p>{t(`blacked.createdBody.${kind}`)}</p>
        <button className="btn primary" onClick={() => openJourneyView(0)}>{t("blacked.showDesign")}<ChevronRight size={15}/></button>
      </>}

      {isJourney && <>
        <span className="blacked-step-icon">{journey[journeyIndex] === "deploy" ? <Rocket/> : journey[journeyIndex] === "data" ? <Database/> : <Sparkles/>}</span>
        <h2 id="blacked-title">{t(`blacked.journey.${journey[journeyIndex]}.title`)}</h2>
        <p>{t(`blacked.journey.${journey[journeyIndex]}.body`)}</p>
        {isLastJourney ? <button className="btn primary" onClick={close}><Check size={15}/>{t("blacked.finish")}</button> : <button className="btn primary" onClick={() => openJourneyView(journeyIndex + 1)}>{t("blacked.next")}<ChevronRight size={15}/></button>}
      </>}
    </div>
  </aside>;
}
